import { Button, Stack, Typography } from '@mui/material'
import { type GridRowId } from '@mui/x-data-grid'
import React, { useState } from 'react'
import { useQueryClient } from 'react-query'

import { ButtonWithConfirmation } from 'components/common/ButtonWithConfirmation'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { ThirdPartyAccountDrawer } from 'components/rbac-settings/teams/integrations/third-party/ThirdPartyAccountDrawer'
import { type TeamExternalIntegrationRow } from 'components/status-checks/common/team-integration-table/team-integration-table-utils'
import { getDeleteExternalIntegrationsStatusCheckAuthenticationParams } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import toast from 'lib/toast'
import { externalIntegrationsQueryKeys } from 'queries/external-integrations/query-keys'
import { useDeleteExternalIntegrationsStatusChecksAuthentication } from 'queries/external-integrations/use-delete-external-integrations-status-checks-authentication'
import { messageFromResponseError } from 'utils/strings'

import type { TeamExternalIntegration } from 'types/api/status-checks/status-check'

export function TeamIntegrationTableActions({
  id,
  isThirdPartyIntegrationsTable,
  onChange,
  integration,
  savedIntegrations
}: {
  children?: never
  id: GridRowId
  isThirdPartyIntegrationsTable?: boolean
  integration: TeamExternalIntegrationRow
  onChange?: () => void
  savedIntegrations: TeamExternalIntegration[]
}) {
  const { evaluatedTeamId } = useStatusCheckExternalIntegrationsContext()
  const queryClient = useQueryClient()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const statusChecksUrls = getStatusChecksUrls()

  const deleteExternalIntegrationsStatusCheckAuthenticationParams =
    getDeleteExternalIntegrationsStatusCheckAuthenticationParams(
      integration,
      evaluatedTeamId
    )

  const { mutateAsync: deleteExternalIntegrationsStatusChecksAuthentication } =
    useDeleteExternalIntegrationsStatusChecksAuthentication(
      deleteExternalIntegrationsStatusCheckAuthenticationParams
    )

  const handleDelete = async () => {
    setIsSubmitting(true)

    try {
      await deleteExternalIntegrationsStatusChecksAuthentication()

      toast({
        type: 'success',
        text: 'Successfully revoked Authentication'
      })

      setIsSubmitting(false)
      if (onChange) {
        onChange()
      }

      await queryClient.invalidateQueries(
        externalIntegrationsQueryKeys.getStatusCheckIntegrations({
          teamId: evaluatedTeamId
        })
      )
    } catch (err) {
      toast({
        type: 'error',
        text: messageFromResponseError(
          err,
          'There was a problem while removing the team integration. Please try again.'
        )
      })
      setIsSubmitting(false)
    }
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      {isThirdPartyIntegrationsTable ? (
        <ThirdPartyAccountDrawer
          externalIntegrations={savedIntegrations}
          mode="edit"
          selectedIntegration={integration}
        />
      ) : (
        <Button
          component={StyledRouterLink}
          data-test-edit-status-check-authentication={id}
          data-track-action="edit-status-check-authentication"
          data-track-category="status-check-authentication"
          data-track-on="click"
          size="small"
          startIcon={<Icon icon="edit" />}
          to={statusChecksUrls.editAuthenticationFromTeamExternalIntegration(
            integration
          )}
          variant="contained"
        >
          Edit
        </Button>
      )}
      <ButtonWithConfirmation
        buttonConfirmProps={{
          color: 'error',
          disabled: isSubmitting,
          startIcon: <Icon icon="trash" />
        }}
        buttonConfirmText="Revoke"
        color="error"
        dialogBodyContent={
          <Typography>
            Are you sure you want to revoke this authentication? This will
            revoke the authentication on this team.
          </Typography>
        }
        dialogTitleContent="Revoke Authentication?"
        size="small"
        startIcon={<Icon icon="trash" />}
        variant="outlined"
        onConfirm={handleDelete}
      >
        Revoke
      </ButtonWithConfirmation>
    </Stack>
  )
}
